import {Box, HBox} from '@buncha/components/core/view';
import {CustomText, H5Text, H6Text} from '@buncha/components/core/text';
import React, {useCallback, useEffect, useState} from 'react';
import {
  timerCounter,
  timerStringInDigits,
} from '@buncha/itemDetails/components/ItemDetail/Counter/counter';
import {useColorThemeType, useFontTheme} from '@buncha/appStyles/hooks/theme';

import {AOOSCatalogItem} from '@buncha/itemDetails/types/SubstituteItemDetails';
import AdjOOSStatus from './AdjOOSStatus';
import {ChatModes} from '@buncha/batchDetails/types/Chat';
import ChatOpener from '@buncha/components/composites/ChatOpener';
import {CustomImage} from '@buncha/components/core/Image';
import HourTimer from '@buncha/components/core/icon/HourTimer';
import If from '@buncha/components/conditional/If';
import ImageWithQuantityPill from '@buncha/common/components/ImageWithQuantityPill';
import {Modules} from '@buncha/utils/types';
import {SharedItem} from '@buncha/batchDetails/types';
import {ShopperScreens} from '@buncha/home/navigation/types';
import {Skeleton} from 'native-base';
import {StyleSheet} from 'react-native';
import SwapIcon from '@buncha/components/core/icon/SwapIcon';
import {Touchable} from '@buncha/components/core/touchable';
import {UserTypes} from '@buncha/onBoarding/types';
import {formatCurrency} from '@buncha/utils/common';
import moment from 'moment';
import {useAppSelector} from '@buncha/config/redux/hooks';
import {useGetLocale} from '@buncha/localization/hooks';

interface Props {
  item: SharedItem;
  aoosChecking: boolean;
  onClick: (
    catalogItemId: number,
    orderItemId: number,
    adjacentOOSId: number,
  ) => void;
  sentCell?: boolean;
}

const LocateItemCell = (props: Props) => {
  const {item, aoosChecking} = props;

  const WAIT_TIME_FOR_AOOS = useAppSelector(
    gState => gState.onboarding.constants?.WAIT_TIME_FOR_AOOS,
  );
  const theme = useColorThemeType();
  const muliFont = useFontTheme('muli');
  const {body} =
    useGetLocale(Modules.batchDetails, ShopperScreens.BatchDetailsPage) ?? {};
  const shopper1Name = item?.originalShopper?.firstName;
  const shopper2Name = item?.newShopper?.firstName;
  const conversationId = item?.conversationId;
  const oldItem = item?.originalItem;
  const newItem = item?.newItem;
  const storeLogo = props.sentCell
    ? item?.newStore?.storeLogoUrl
    : item?.originalStore?.storeLogoUrl;
  const newStoreName = item?.newStore?.name;
  const newQuantity = item?.orderItem?.quantity;
  const oldQuantity = item?.orderItem?.substitutedBy?.quantity;
  const minutes = parseInt(WAIT_TIME_FOR_AOOS ?? '0', 10) ?? 10;
  const foundAt = item?.adjacentOOS?.foundAt;
  const dateWithOffset = new Date(
    new Date(foundAt).getTime() + minutes * 60 * 1000,
  );
  const date = moment(dateWithOffset);
  const initDaysToExpire = date.diff(moment());
  const initEventDate = moment.duration(initDaysToExpire);
  const [timer, setTimer] = useState({
    hours: initEventDate.hours(),
    mins: initEventDate.minutes(),
    secs: initEventDate.seconds(),
  });

  const timerFunc = useCallback(() => {
    const {hours, mins, secs} = timerCounter(date);
    setTimer({
      hours,
      mins,
      secs,
    });
  }, [date]);

  const counter = timerStringInDigits(timer);

  const handleOnClick = useCallback(() => {
    props.onClick(item.newItem?.id, item?.orderItem?.id, item?.adjacentOOS?.id);
  }, [item?.adjacentOOS?.id, item.newItem?.id, item?.orderItem?.id, props]);

  const getCell = useCallback(
    (aoosItem: AOOSCatalogItem, quantity: number) => (
      <HBox style={styles.alignHBox}>
        <ImageWithQuantityPill
          imageLink={aoosItem?.itemImageUrl}
          quantity={quantity}
        />
        <Box style={styles.textContainer}>
          <H5Text
            style={[
              styles.textAlignLeft,
              {
                color: theme?.text?.blue?.[300],
              },
            ]}>
            {aoosItem?.name}
          </H5Text>
          <H5Text
            style={[
              styles.textAlignLeft,
              {
                color: theme?.text?.blue?.[300],
              },
            ]}>
            {formatCurrency((aoosItem?.price ?? 0) * (quantity ?? 1))}
          </H5Text>
        </Box>
      </HBox>
    ),
    [theme?.text?.blue],
  );

  useEffect(() => {
    if (date) {
      const startTimer = setInterval(() => {
        timerFunc();
      }, 1000);
      return () => {
        clearInterval(startTimer);
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [date]);

  return (
    <Box
      style={[
        styles.mainContainer,
        styles.lightBorderBox,
        {
          borderColor: props?.sentCell
            ? theme?.text?.blue?.[100]
            : theme?.text?.gray[200],
        },
      ]}>
      <If condition={props.sentCell}>
        <AdjOOSStatus
          foundAt={dateWithOffset}
          status={item?.shopperStatus}
          shopper2={item?.newShopper?.firstName}
        />
      </If>
      <HBox
        style={[
          styles.lightGreyBox,
          styles.shopperDetails,
          {
            borderColor: theme?.text?.gray?.[200],
          },
        ]}>
        <HBox style={styles.shopperDetailsInnerBox}>
          <CustomImage
            source={storeLogo ?? ''}
            resizeMode="contain"
            style={[
              styles.storeLogo,
              {
                borderColor: theme?.text?.gray?.[200],
              },
            ]}
            onErrorSource={''}
          />
          <Box style={styles.shopperStatusBox}>
            <H6Text
              style={[
                styles.textAlignLeft,
                {
                  fontFamily: muliFont[600].normal,
                  color: theme?.text?.blue[300],
                },
              ]}>
              <If condition={props.sentCell}>
                {body?.locateItemCell?.header
                  ?.replace?.('{shopper2Name}', shopper2Name)
                  ?.replace?.('{newStoreName}', newStoreName)}
              </If>
              <If condition={!props.sentCell}>
                {body?.locateItemCell?.shopper2Header?.replace?.(
                  '{shopper1Name}',
                  shopper1Name,
                )}
              </If>
            </H6Text>
            <CustomText
              style={[
                styles.textAlignLeft,
                {
                  color: theme?.text.gray[100],
                  fontFamily: muliFont[600].normal,
                },
              ]}>
              {body?.locateItemCell?.subHeader?.replace?.(
                '{shopperName}',
                props.sentCell ? shopper2Name : shopper1Name,
              )}
            </CustomText>
          </Box>
          <ChatOpener
            hideRole={UserTypes.shopper}
            mode={ChatModes.order}
            conversationId={conversationId}
            height={30}
            width={50}
            style={styles.chatIcon}
            iconWidth={142}
          />
        </HBox>
      </HBox>
      <H6Text
        style={[
          styles.textAlignLeft,
          {
            fontFamily: muliFont[700].normal,
            color: theme?.text?.blue[300],
          },
        ]}>
        {body?.locateItemCell?.originalItem}
      </H6Text>
      {getCell(oldItem, oldQuantity)}
      <HBox style={styles.alignHBox}>
        <SwapIcon width={20} height={20} />
        <H6Text
          style={[
            styles.textAlignLeft,
            {
              fontFamily: muliFont[700].normal,
              color: theme?.text?.blue[300],
            },
          ]}>
          {body?.locateItemCell?.swappingWith}
        </H6Text>
      </HBox>
      {getCell(newItem, newQuantity)}

      <If condition={Boolean(counter) && initDaysToExpire > 0}>
        <HBox
          style={[
            styles.lightBorderBox,
            {
              borderColor: theme?.text?.gray?.[200],
            },
          ]}>
          <H6Text
            style={[
              styles.textAlignLeft,
              {
                fontFamily: muliFont[600].normal,
                color: theme?.text?.gray[100],
              },
            ]}>
            {body?.locateItemCell?.autoApprovedIn}
          </H6Text>
          <HBox>
            <HourTimer
              fill={theme?.text.gray[100]}
              height={24}
              width={24}
              viewBox="0 -4 28 28"
            />
            <H6Text
              style={[
                styles.text,
                {
                  color: theme?.text.gray[100],
                  fontFamily: muliFont[600].normal,
                },
              ]}>
              {counter}
            </H6Text>
          </HBox>
        </HBox>
      </If>

      <Touchable onPress={handleOnClick}>
        <If condition={!aoosChecking}>
          <CustomText
            style={[
              styles.moreDetails,
              {
                color: theme?.text.blue[300],
                fontFamily: muliFont[900].normal,
              },
            ]}>
            <If condition={props.sentCell}>
              {body?.locateItemCell?.viewItemDetails}
            </If>
            <If condition={!props.sentCell}>
              {body?.locateItemCell?.viewMoreDetails}
            </If>
          </CustomText>
        </If>
        <If condition={aoosChecking}>
          <Skeleton style={styles.loadingSkeleton} />
        </If>
      </Touchable>
    </Box>
  );
};

export default React.memo(LocateItemCell);

const styles = StyleSheet.create({
  chatIcon: {
    height: 30,
  },
  lightGreyBox: {
    borderWidth: 1,
    borderRadius: 10,
  },
  lightBorderBox: {
    borderWidth: 1,
    borderRadius: 10,
    padding: 10,
  },
  shopperDetails: {
    display: 'flex',
    alignItems: 'center',
    paddingVertical: '2%',
  },
  shopperDetailsInnerBox: {
    gap: 10,
    flex: 1,
    justifyContent: 'flex-start',
    alignItems: 'center',
  },
  storeLogo: {
    borderRadius: 50,
    height: 32,
    width: 32,
    overflow: 'hidden',
    borderWidth: 1,
    marginLeft: 5,
  },
  textAlignLeft: {
    textAlign: 'left',
  },
  shopperStatusBox: {flex: 1, gap: 2},
  mainContainer: {
    paddingVertical: 10,
    gap: 10,
  },
  alignHBox: {
    justifyContent: 'flex-start',
    alignItems: 'flex-start',
    gap: 10,
  },
  text: {
    textAlign: 'left',
    marginVertical: 1,
    marginLeft: 3,
  },
  skeleton: {
    width: 70,
    height: 25,
    borderRadius: 8,
  },
  loadingSkeleton: {
    width: 120,
    height: 20,
    alignSelf: 'center',
  },
  moreDetails: {
    textDecorationLine: 'underline',
    marginTop: 10,
  },
  textContainer: {flex: 1},
});
